import React, { useState, useEffect, useCallback } from 'react';
import LocationPicker from './LocationPicker';

const BusinessAddressPicker = ({ address, onAddressChange }) => {
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [manualAddress, setManualAddress] = useState({
    street: address?.street || '',
    city: address?.city || '',
    state: address?.state || '',
    zipcode: address?.zipcode || '',
    coordinates: address?.coordinates || { latitude: 0, longitude: 0 }
  });

  // Update local state when props change (initial load only)
  useEffect(() => {
    if (address) {
      setManualAddress({
        street: address.street || '',
        city: address.city || '',
        state: address.state || '',
        zipcode: address.zipcode || '',
        coordinates: address.coordinates || { latitude: 0, longitude: 0 }
      });
    }
  }, [
    address?.street, 
    address?.city, 
    address?.state, 
    address?.zipcode, 
    address?.coordinates?.latitude, 
    address?.coordinates?.longitude
  ]);

  const handleMapLocationSelect = (locationData) => {
    const newAddress = {
      street: locationData.street,
      city: locationData.city,
      state: locationData.state,
      zipcode: locationData.zipcode,
      coordinates: locationData.coordinates
    };
    setManualAddress(newAddress);
    onAddressChange(newAddress);
    setShowMapPicker(false);
  };

  const handleManualInputChange = (field, value) => {
    const updatedAddress = {
      ...manualAddress,
      [field]: value,
      // Reset coordinates when manual input changes (except coordinates field)
      coordinates: field !== 'coordinates' ? { latitude: 0, longitude: 0 } : manualAddress.coordinates
    };
    setManualAddress(updatedAddress);
    onAddressChange(updatedAddress);
  };

  const hasCoordinates = manualAddress.coordinates && 
    manualAddress.coordinates.latitude !== 0 && 
    manualAddress.coordinates.longitude !== 0;

  return (
    <div className="space-y-4">
      {/* Manual Address Input */}
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Street Address
          </label>
          <input
            type="text"
            placeholder="123 Main Street"
            value={manualAddress.street}
            onChange={(e) => handleManualInputChange('street', e.target.value)}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City *
            </label>
            <input
              type="text"
              placeholder="New York"
              value={manualAddress.city}
              onChange={(e) => handleManualInputChange('city', e.target.value)}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State *
            </label>
            <select
              value={manualAddress.state}
              onChange={(e) => handleManualInputChange('state', e.target.value)}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
              required
            >
              <option value="">Select State</option>
              <option value="Alabama">Alabama</option>
              <option value="Alaska">Alaska</option>
              <option value="Arizona">Arizona</option>
              <option value="Arkansas">Arkansas</option>
              <option value="California">California</option>
              <option value="Colorado">Colorado</option>
              <option value="Connecticut">Connecticut</option>
              <option value="Delaware">Delaware</option>
              <option value="Florida">Florida</option>
              <option value="Georgia">Georgia</option>
              <option value="Hawaii">Hawaii</option>
              <option value="Idaho">Idaho</option>
              <option value="Illinois">Illinois</option>
              <option value="Indiana">Indiana</option>
              <option value="Iowa">Iowa</option>
              <option value="Kansas">Kansas</option>
              <option value="Kentucky">Kentucky</option>
              <option value="Louisiana">Louisiana</option>
              <option value="Maine">Maine</option>
              <option value="Maryland">Maryland</option>
              <option value="Massachusetts">Massachusetts</option>
              <option value="Michigan">Michigan</option>
              <option value="Minnesota">Minnesota</option>
              <option value="Mississippi">Mississippi</option>
              <option value="Missouri">Missouri</option>
              <option value="Montana">Montana</option>
              <option value="Nebraska">Nebraska</option>
              <option value="Nevada">Nevada</option>
              <option value="New Hampshire">New Hampshire</option>
              <option value="New Jersey">New Jersey</option>
              <option value="New Mexico">New Mexico</option>
              <option value="New York">New York</option>
              <option value="North Carolina">North Carolina</option>
              <option value="North Dakota">North Dakota</option>
              <option value="Ohio">Ohio</option>
              <option value="Oklahoma">Oklahoma</option>
              <option value="Oregon">Oregon</option>
              <option value="Pennsylvania">Pennsylvania</option>
              <option value="Rhode Island">Rhode Island</option>
              <option value="South Carolina">South Carolina</option>
              <option value="South Dakota">South Dakota</option>
              <option value="Tennessee">Tennessee</option>
              <option value="Texas">Texas</option>
              <option value="Utah">Utah</option>
              <option value="Vermont">Vermont</option>
              <option value="Virginia">Virginia</option>
              <option value="Washington">Washington</option>
              <option value="West Virginia">West Virginia</option>
              <option value="Wisconsin">Wisconsin</option>
              <option value="Wyoming">Wyoming</option>
            </select>
          </div>
        </div>
        
        <div className="grid grid-cols-1">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ZIP Code
            </label>
            <input
              type="text"
              placeholder="10001"
              value={manualAddress.zipcode}
              onChange={(e) => handleManualInputChange('zipcode', e.target.value)}
              className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>
      </div>

      {/* Map Integration Section */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h5 className="text-sm font-medium text-gray-900">Location Coordinates</h5>
            <p className="text-xs text-gray-500">
              {hasCoordinates 
                ? 'Precise location coordinates are set' 
                : 'Add coordinates for precise location mapping'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowMapPicker(!showMapPicker)}
            className={`px-3 py-1 text-sm rounded-md ${
              showMapPicker 
                ? 'bg-gray-200 text-gray-700' 
                : 'bg-orange-600 text-white hover:bg-orange-700'
            }`}
          >
            {showMapPicker ? 'Hide Map' : 'Select on Map'}
          </button>
        </div>

        {/* Coordinates Status */}
        {hasCoordinates && (
          <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-3">
            <div className="flex items-center">
              <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm font-medium text-green-800">
                Location coordinates set
              </span>
            </div>
            <p className="text-xs text-green-600 mt-1">
              Lat: {manualAddress.coordinates.latitude.toFixed(6)}, 
              Lng: {manualAddress.coordinates.longitude.toFixed(6)}
            </p>
          </div>
        )}

        {!hasCoordinates && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-3">
            <div className="flex items-center">
              <svg className="w-4 h-4 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="text-sm font-medium text-yellow-800">
                No coordinates set
              </span>
            </div>
            <p className="text-xs text-yellow-600 mt-1">
              Use the map to set precise location coordinates for better job matching
            </p>
          </div>
        )}

        {/* Map Picker */}
        {showMapPicker && (
          <div className="border border-gray-200 rounded-lg p-4">
            <LocationPicker
              onLocationSelect={handleMapLocationSelect}
              initialLocation={hasCoordinates ? {
                lat: manualAddress.coordinates.latitude,
                lng: manualAddress.coordinates.longitude
              } : null}
              businessAddress={manualAddress}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessAddressPicker; 